/**
 * @graphql experimental
 */
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import gql from 'graphql-tag';

import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization';

import type {
	useIsNav4ChangeboardingQuery as useIsNav4ChangeboardingQuery$data,
	useIsNav4ChangeboardingQueryVariables as useIsNav4ChangeboardingQuery$variables,
} from './__types__/useIsNav4ChangeboardingQuery';

const Nav4ChangeboardingQuery =
	// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
	gql`
		query useIsNav4ChangeboardingQuery($cloudId: String!, $userId: String!) @experimental {
			experimentalSiteUserPersonalization(cloudId: $cloudId, userId: $userId) {
				attributes {
					name
					value
				}
			}
		}
	`;

const NAV_4_CHANGEBOARDING_ENROLLED_TRAIT =
	'segment_confluence_nav_4_changeboarding_cloned_siteuser';
const NAV_4_CHANGEBOARDING_COMPLETED_TRAIT = 'completed_cc-nav-4-changeboarding-prod';

const isExpectedError = (error: ApolloErrorInterop | undefined) => {
	if (isUnauthorizedError(error)) {
		// 401s are expected when users don't have permissions
		return true;
	}
	return false;
};

export const useIsNav4Changeboarding = () => {
	const { cloudId, userId } = useSessionData();

	const isNav4Enabled = useIsNav4Enabled();

	const queryArgs = {
		variables: { cloudId, userId: userId! },
		skip: !userId || !isNav4Enabled,
	};

	const { data, loading, error } = expValEquals(
		'confluence_deprecate_experimental_schema_exp_2',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalSiteUserPersonalization(queryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<useIsNav4ChangeboardingQuery$data, useIsNav4ChangeboardingQuery$variables>(
				Nav4ChangeboardingQuery,
				queryArgs,
			);

	if (error && isExpectedError(error) && fg('cc_engagement_team_changeboarding_handle_401_error')) {
		// Suppress error from Splunk
		markErrorAsHandled(error);
	}

	if (!isNav4Enabled) {
		return {
			isUserEnrolledInNav4Changeboarding: false,
			isUserCompletedNav4Changeboarding: false,
		};
	}

	const attributes = data?.experimentalSiteUserPersonalization?.attributes;

	const isUserEnrolledInNav4Changeboarding = Boolean(
		attributes?.some(
			({ name, value }: { name: string; value: string }) =>
				name === NAV_4_CHANGEBOARDING_ENROLLED_TRAIT && value === 'true',
		),
	);

	const isUserCompletedNav4Changeboarding = Boolean(
		attributes?.some(
			({ name, value }: { name: string; value: string }) =>
				name === NAV_4_CHANGEBOARDING_COMPLETED_TRAIT && !!value,
		),
	);

	return {
		isUserEnrolledInNav4Changeboarding,
		isUserCompletedNav4Changeboarding,
		loading,
		error,
	};
};
